import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { MaintenanceIntervalType } from '../../../../types/Enums';
import { ComponentWithReplacement, ComponentMaintenanceDataWithDone } from '../types';
import { ComponentLifetime, MaintenanceTemplate, getIntervalForDirection, hasIntervalForDirection } from '../../TemplatesEditor/types';
import { resolveCardinalDirection } from '../utils/cardinalDirectionResolver';
import { Building } from '../../../../types/protocols';
import { CostSettingsContext, ReplacementCostContext } from '../hooks/useModalManagement';
import { IntervalSettingsContext } from '../hooks/useIntervalModal';
import * as validation from '../utils/validation';
import { calculateNextMaintenanceDate } from '../utils/calculations';
import { useBuildComponentTypes } from '../../../../contexts/BuildComponentTypesContext';
import type { CheckRow } from '../../../../Helpers/componentChecks';
import {
    calculateActivityCost,
    calculateReplacementCost,
    CostSource,
    MaintenanceActivity,
    replacementQuantityDisplay,
    ReplacementQuantityDisplay,
    unitCountOf
} from '../../../../Helpers/maintenanceCostUtils';
import { keysToToggle, rowPresence, rowTargets, sumDisplayCosts, unanimous, unanimousOrNull } from '../utils/bulkSelection';

/** What a row's cost rests on, for the cost chip */
export interface CostBasis {
    source: CostSource;
    hasAttachments: boolean;
}

/** Massredigering: the card edits every selected component at once */
export interface BulkCardState {
    keys: string[];
    plans: ComponentWithReplacement[];
    doneCount: number;
    excludedCount: number;
    /** The selected components without skick or livslängd: only Ändra skick is offered while any exist */
    incompletePlans: ComponentWithReplacement[];
    /** How many of the selected components carry the row */
    rowPresence: (templateId: string) => { present: number; total: number };
}

/**
 * Context value for ComponentCard - provides all handlers, display helpers, and UI state
 * bound to the currently selected component, or in Massredigering to every selected one
 */
interface ComponentCardContextValue {
    // Component identification
    componentKey: string;

    // Core data
    plan: ComponentWithReplacement;
    buildingKey: string;
    building: Building | null;
    templates: MaintenanceTemplate[];
    lifetime: ComponentLifetime | null;
    draftComponents: { [componentKey: string]: ComponentMaintenanceDataWithDone };
    /** Null in Enskild mode */
    bulk: BulkCardState | null;

    // Draft state
    isDone: boolean;
    hasIncompleteInfo: boolean;
    isExcluded: boolean;

    // Validation states
    isReplacementYearInvalid: boolean;
    getMaintenanceDateInvalid: (templateId: string) => boolean;
    getMaintenanceIntervalInvalid: (templateId: string) => boolean;

    // Display helpers
    getDisplayReplacementYear: string;
    getDisplayReplacementCost: string;
    replacementQuantity: ReplacementQuantityDisplay | null;
    /** The replacement's own antal (kr/st); per component, undefined in Massredigering and when absent */
    replacementUnitCount: number | undefined;
    /** Set or remove the replacement antal; per component, never in bulk */
    onReplacementUnitCountChange: (count: number) => void;
    onReplacementUnitCountClear: () => void;
    replacementCostBasis: CostBasis;
    getDisplayLifetime: string;
    getDisplayMaintenanceInterval: (templateId: string) => string;
    getDisplayMaintenanceDate: (templateId: string) => string;
    getDisplayMaintenanceCost: (templateId: string) => string;
    getDisplayBaseInterval: (templateId: string) => { value: string; type: MaintenanceIntervalType };
    getDisplayIntervalType: (templateId: string) => MaintenanceIntervalType;

    // Cost explanations
    getReplacementCostExplanation: string;
    getMaintenanceCostExplanation: (templateId: string) => string;

    // Handlers
    onOpenConditionModal: () => void;
    onOpenLifetimeModal: () => void;
    onReplacementYearChange: (value: string) => void;
    onReplacementYearBlur: () => void;
    onReplacementCostChange: (value: string) => void;
    onReplacementCostBlur: () => void;
    onMaintenanceRemove: (templateId: string) => void;
    /** Massredigering: puts a row that only some selected components have on the rest */
    onAddRowToAll: (templateId: string) => void;
    onMaintenanceIntervalChange: (templateId: string, value: string) => void;
    onMaintenanceIntervalBlur: (templateId: string) => void;
    onMaintenanceIntervalTypeChange: (templateId: string, type: MaintenanceIntervalType) => void;
    onMaintenanceDateChange: (templateId: string, value: string) => void;
    onMaintenanceDateBlur: (templateId: string) => void;
    onMaintenanceCostChange: (templateId: string, value: string) => void;
    onMaintenanceCostBlur: (templateId: string) => void;
    onDoneToggle: () => void;
    onExcludeToggle: () => void;
    onOpenNewActivityModal: () => void;
    onOpenCostSettingsModal: (templateId: string) => void;
    onOpenReplacementCostModal: () => void;
    onOpenIntervalSettingsModal: (templateId: string) => void;
    /** Absent for non-admins, who cannot edit templates */
    onOpenEditTemplateModal?: (template: MaintenanceTemplate) => void;
    onOpenAreaModal: () => void;
    onOpenBulkApplyModal: () => void;
    hasBulkApplyTargets: boolean;
    onOpenCopyActivitiesModal: () => void;
    /** Another component of the same base type has activities to copy from */
    hasCopyActivitiesSources: boolean;

    // Kontroller: the building checks about this component, one row per template (live data, not part of the plan)
    coveringChecks: CheckRow[];
    onOpenAddCheck: () => void;
    /** Absent for non-admins */
    onRemoveCheck?: (row: CheckRow) => void;
    onEditCheckDate: (row: CheckRow) => void;

    // Custom quantity handlers: area/length override for per_sqm/per_lm, antal for per_unit
    onQuantityChange: (templateId: string, quantity: number) => void;
    onQuantityClear: (templateId: string) => void;
    getActivityCustomQuantity: (templateId: string) => number | undefined;
    getActivityComment: (templateId: string) => string | undefined;
    /** The draft stores an entry for the row (only components with a condition and a lifetime get one) */
    hasActivityEntry: (templateId: string) => boolean;
    onCommentChange: (templateId: string, text: string) => void;
    getDisplayArea: (templateId: string) => string;
    /** The component's own quantity in the template's cost unit (area for per_sqm, length for per_lm, 1 for per_unit) */
    getComponentQuantity: (templateId: string) => number | null;
    getActivityHasCustomCost: (templateId: string) => boolean;
    getActivityCostBasis: (templateId: string) => CostBasis;
    getTemplateCostUnit: (templateId: string) => 'per_sqm' | 'per_lm' | 'per_unit';

    // UI state
    highlightedActivity: string | null;
    setHighlightedActivity: (id: string | null) => void;
    activeTooltip: string | null;
    setActiveTooltip: (id: string | null) => void;

    // Maintenance checking
    isDueThisYear: (nextDate: Date | null) => boolean;
    isCustomInterval: (templateId: string) => boolean;
    calculateNextMaintenanceDate: (templateId: string) => Date | null;
}

const ComponentCardContext = createContext<ComponentCardContextValue | null>(null);

/**
 * Hook to access the ComponentCard context
 * @throws Error if used outside of ComponentCardProvider
 */
export const useComponentCardContext = (): ComponentCardContextValue => {
    const context = useContext(ComponentCardContext);
    if (!context) {
        throw new Error('useComponentCardContext must be used within a ComponentCardProvider');
    }
    return context;
};

/**
 * Props for ComponentCardProvider
 */
interface ComponentCardProviderProps {
    children: React.ReactNode;

    // Core data
    selectedPlan: ComponentWithReplacement;
    /** Massredigering: every selected component (same specificType); `selectedPlan` is the first of them */
    bulkPlans?: ComponentWithReplacement[];
    buildingKey: string;
    building: Building | null;
    templates: MaintenanceTemplate[];
    lifetime: ComponentLifetime | null;
    draftComponents: { [componentKey: string]: ComponentMaintenanceDataWithDone };

    // Template lookup for cross-type templates
    getTemplateById?: (id: string) => MaintenanceTemplate | null;

    // UI state from parent
    highlightedActivity: string | null;
    setHighlightedActivity: (id: string | null) => void;
    activeTooltip: string | null;
    setActiveTooltip: (id: string | null) => void;

    // Handlers from hooks - these take componentKey as first param
    handleOpenConditionModal: (plans: ComponentWithReplacement[]) => void;
    handleOpenLifetimeModal: (componentType: string) => void;
    handleOpenNewActivityModal: (componentType: string, componentKeys: string[]) => void;
    handleReplacementYearChange: (componentKey: string, value: string) => void;
    handleReplacementYearBlur: (componentKey: string) => void;
    handleReplacementCostChange: (componentKey: string, value: string) => void;
    handleReplacementCostBlur: (componentKey: string) => void;
    /** Asks to remove an activity row from the components; `isTypeTemplate` tells a row of the component type's own templates from one added by hand */
    handleMaintenanceActivityRemove: (componentKeys: string[], templateId: string, isTypeTemplate: boolean) => void;
    handleMaintenanceActivityAdd: (componentKey: string, templateId: string) => void;
    handleMaintenanceIntervalChange: (componentKey: string, templateId: string, value: string) => void;
    handleMaintenanceIntervalBlur: (componentKey: string, templateId: string) => void;
    handleMaintenanceIntervalTypeChange: (componentKey: string, templateId: string, type: MaintenanceIntervalType) => void;
    handleMaintenanceDateChange: (componentKey: string, templateId: string, value: string) => void;
    handleMaintenanceDateBlur: (componentKey: string, templateId: string) => void;
    handleMaintenanceCostChange: (componentKey: string, templateId: string, value: string) => void;
    handleMaintenanceCostBlur: (componentKey: string, templateId: string) => void;
    handleDoneToggle: (componentKey: string) => void;
    handleExcludeToggle: (componentKey: string) => void;
    handleOpenCostSettingsModal: (context: CostSettingsContext) => void;
    handleOpenReplacementCostModal: (context: ReplacementCostContext) => void;
    handleOpenIntervalSettingsModal: (context: IntervalSettingsContext) => void;
    /** Absent for non-admins, who cannot edit templates. `componentKeys`: the row's components (the selected ones in Massredigering) */
    handleOpenEditTemplateModal?: (template: MaintenanceTemplate, componentKeys: string[]) => void;
    handleOpenAreaModal: (plan: ComponentWithReplacement) => void;
    handleOpenBulkApplyModal: (sourceKey: string) => void;
    hasBulkApplyTargets: boolean;
    handleOpenCopyActivitiesModal: (targetKey: string) => void;
    hasCopyActivitiesSources: boolean;
    coveringChecks: CheckRow[];
    handleOpenAddCheck: (componentKeys: string[]) => void;
    handleRemoveCheck?: (row: CheckRow) => void;
    handleEditCheckDate: (row: CheckRow) => void;
    handleActivityQuantityChange: (componentKey: string, templateId: string, quantity: number) => void;
    handleActivityQuantityClear: (componentKey: string, templateId: string) => void;
    handleActivityUnitCountChange: (componentKey: string, templateId: string, count: number) => void;
    handleActivityUnitCountClear: (componentKey: string, templateId: string) => void;
    handleReplacementUnitCountChange: (componentKey: string, count: number) => void;
    handleReplacementUnitCountClear: (componentKey: string) => void;
    handleActivityCommentChange: (componentKey: string, templateId: string, text: string) => void;

    // Display helpers from hooks
    getDisplayReplacementYear: (plan: ComponentWithReplacement) => string;
    getDisplayReplacementCost: (componentKey: string) => string;
    getDisplayLifetime: (componentKey: string, lifetime: ComponentLifetime) => string;
    getDisplayMaintenanceInterval: (componentKey: string, templateId: string, template: MaintenanceTemplate) => string;
    getDisplayMaintenanceDate: (componentKey: string, templateId: string, nextDate: Date | null) => string;
    getDisplayMaintenanceCost: (componentKey: string, templateId: string) => string;
    getDisplayBaseInterval: (componentKey: string, template: MaintenanceTemplate) => { value: string; type: MaintenanceIntervalType };
    getReplacementCostExplanation: (componentKey: string) => string;
    getMaintenanceCostExplanation: (componentKey: string, templateId: string) => string;
}

/** The cost settings of a row that a bulk cost modal is pre-filled with when they agree */
const costSettingsKey = (activity: MaintenanceActivity | undefined): string => JSON.stringify([
    activity?.cost, activity?.costSource, activity?.customUnitPrice, activity?.costComment,
    activity?.investmentPercentage, Object.keys(activity?.costAttachments ?? {}).sort()
]);

/** A row's cost settings stripped: what a bulk cost modal starts from when the rows disagree */
const withoutCostSettings = (activity: MaintenanceActivity): MaintenanceActivity => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { cost, costSource, customUnitPrice, costComment, costAttachments, investmentPercentage, ...rest } = activity;
    return rest;
};

const replacementSettingsKey = (data: ComponentMaintenanceDataWithDone | undefined): string => JSON.stringify([
    data?.replacementCost, data?.replacementCostSource, data?.replacementCustomUnitPrice, data?.replacementCostComment,
    data?.replacementInvestmentPercentage, Object.keys(data?.replacementAttachments ?? {}).sort()
]);

const sumOrNull = (values: Array<number | null>): number | null =>
    values.every(v => v !== null) ? values.reduce<number>((sum, v) => sum + (v as number), 0) : null;

/**
 * Provider that binds all handlers and display helpers to the selected component
 * Reduces prop drilling from 40+ props to a context value.
 *
 * With `bulkPlans` (Massredigering) every handler is applied to each selected
 * component, row edits to the components that have the row, and a display
 * value the components disagree on reads "Blandade". Costs and quantities are
 * summed over the selection.
 */
export const ComponentCardProvider: React.FC<ComponentCardProviderProps> = ({
    children,
    selectedPlan,
    bulkPlans,
    buildingKey,
    building,
    templates,
    lifetime,
    draftComponents,
    getTemplateById,
    highlightedActivity,
    setHighlightedActivity,
    activeTooltip,
    setActiveTooltip,
    handleOpenConditionModal,
    handleOpenLifetimeModal,
    handleOpenNewActivityModal,
    handleReplacementYearChange,
    handleReplacementYearBlur,
    handleReplacementCostChange,
    handleReplacementCostBlur,
    handleMaintenanceActivityRemove,
    handleMaintenanceActivityAdd,
    handleMaintenanceIntervalChange,
    handleMaintenanceIntervalBlur,
    handleMaintenanceIntervalTypeChange,
    handleMaintenanceDateChange,
    handleMaintenanceDateBlur,
    handleMaintenanceCostChange,
    handleMaintenanceCostBlur,
    handleDoneToggle,
    handleExcludeToggle,
    handleOpenCostSettingsModal,
    handleOpenReplacementCostModal,
    handleOpenIntervalSettingsModal,
    handleOpenEditTemplateModal,
    handleOpenAreaModal,
    handleOpenBulkApplyModal,
    hasBulkApplyTargets,
    handleOpenCopyActivitiesModal,
    hasCopyActivitiesSources,
    coveringChecks,
    handleOpenAddCheck,
    handleRemoveCheck,
    handleEditCheckDate,
    handleActivityQuantityChange,
    handleActivityQuantityClear,
    handleActivityUnitCountChange,
    handleActivityUnitCountClear,
    handleReplacementUnitCountChange,
    handleReplacementUnitCountClear,
    handleActivityCommentChange,
    getDisplayReplacementYear,
    getDisplayReplacementCost,
    getDisplayLifetime,
    getDisplayMaintenanceInterval,
    getDisplayMaintenanceDate,
    getDisplayMaintenanceCost,
    getDisplayBaseInterval,
    getReplacementCostExplanation,
    getMaintenanceCostExplanation
}) => {
    const { getTranslation } = useBuildComponentTypes();
    const componentKey = selectedPlan.component.key;
    const specificTypeValue = selectedPlan.component.specificType?.value;

    // The components the card edits: the one selected, or every ticked one
    const plans = useMemo(() => (bulkPlans && bulkPlans.length > 0 ? bulkPlans : [selectedPlan]), [bulkPlans, selectedPlan]);
    const keys = useMemo(() => plans.map(p => p.component.key), [plans]);
    const isBulk = bulkPlans !== undefined;

    // Compute combined templates: type-specific templates + cross-type templates from maintenanceActivities,
    // without the type's rows the user removed from the component (from every selected component, in bulk)
    const allTemplates = useMemo(() => {
        const templateIds = new Set(templates.map(t => t.id));
        const result = templates.filter(t => !keys.every(key => draftComponents[key]?.removedActivities?.[t.id] === true));

        // Look for activities with template IDs not in the type-specific templates
        keys.forEach(key => {
            const activities = draftComponents[key]?.maintenanceActivities;
            if (!activities || !getTemplateById) return;
            Object.keys(activities).forEach(templateId => {
                if (!templateIds.has(templateId)) {
                    // This is a cross-type template - look it up
                    const crossTypeTemplate = getTemplateById(templateId);
                    if (crossTypeTemplate) {
                        result.push(crossTypeTemplate);
                        templateIds.add(templateId);
                    }
                }
            });
        });

        return result;
    }, [templates, draftComponents, keys, getTemplateById]);

    const isPerUnitTemplate = useCallback((templateId: string) =>
        allTemplates.find(t => t.id === templateId)?.cost_unit === 'per_unit', [allTemplates]);

    // Memoize the context value to prevent unnecessary re-renders
    const value = useMemo<ComponentCardContextValue>(() => {
        const each = (fn: (key: string) => void) => keys.forEach(fn);
        const rowKeys = (templateId: string) => rowTargets(keys, draftComponents, templateId);
        const eachRow = (templateId: string, fn: (key: string) => void) => rowKeys(templateId).forEach(fn);
        const over = <T,>(fn: (plan: ComponentWithReplacement) => T): T[] => plans.map(fn);
        const overRow = <T,>(templateId: string, fn: (key: string) => T): T[] => rowKeys(templateId).map(fn);
        const templateOf = (templateId: string) => allTemplates.find(t => t.id === templateId);
        const activityOf = (key: string, templateId: string) => draftComponents[key]?.maintenanceActivities?.[templateId];
        const firstActivity = (templateId: string) => activityOf(rowKeys(templateId)[0], templateId);

        // A row's quantity in the template's unit: the activity's own, else the component's
        const rowQuantity = (plan: ComponentWithReplacement, templateId: string): number | null => {
            const template = templateOf(templateId);
            if (!template) return null;
            const activity = activityOf(plan.component.key, templateId);
            if (template.cost_unit === 'per_unit') return unitCountOf(activity?.customUnitCount);
            const custom = activity?.customQuantity;
            if (custom !== undefined && custom > 0) return custom;
            if (template.cost_unit === 'per_lm') return plan.component.linearMeters?.value || null;
            return building ? plan.component.totalArea(building) : null;
        };
        const unitLabelOf = (templateId: string) => {
            const unit = templateOf(templateId)?.cost_unit;
            return unit === 'per_unit' ? 'st' : unit === 'per_lm' ? 'lm' : 'm²';
        };

        const incompletePlans = plans.filter(p => !p.hasCondition || !p.hasLifetime);
        const bulk: BulkCardState | null = isBulk ? {
            keys,
            plans,
            doneCount: keys.filter(key => draftComponents[key]?.isDone === true).length,
            excludedCount: keys.filter(key => draftComponents[key]?.excluded === true).length,
            incompletePlans,
            rowPresence: (templateId: string) => rowPresence(keys, draftComponents, templateId)
        } : null;

        return {
            // Component identification
            componentKey,

            // Core data
            plan: selectedPlan,
            buildingKey,
            building,
            templates: allTemplates,
            lifetime,
            draftComponents,
            bulk,

            // Draft state
            isDone: keys.every(key => draftComponents[key]?.isDone === true),
            hasIncompleteInfo: incompletePlans.length > 0,
            isExcluded: keys.every(key => draftComponents[key]?.excluded === true),

            // Validation states
            isReplacementYearInvalid: keys.some(key => validation.isReplacementYearInvalid(key, draftComponents)),
            getMaintenanceDateInvalid: (templateId: string) =>
                keys.some(key => validation.isMaintenanceDateInvalid(key, templateId, draftComponents)),
            getMaintenanceIntervalInvalid: (templateId: string) =>
                keys.some(key => validation.isMaintenanceIntervalInvalid(key, templateId, draftComponents)),

            // Display helpers: one value, or "Blandade" where the selection disagrees; costs summed
            getDisplayReplacementYear: unanimous(over(getDisplayReplacementYear)),
            getDisplayReplacementCost: sumDisplayCosts(keys.map(getDisplayReplacementCost)),
            replacementCostBasis: {
                source: unanimousOrNull(keys.map(key => draftComponents[key]?.replacementCostSource ?? 'template')) ?? 'template',
                hasAttachments: keys.some(key => Object.keys(draftComponents[key]?.replacementAttachments ?? {}).length > 0)
            },
            replacementQuantity: lifetime
                ? replacementQuantityDisplay(
                    lifetime.costUnit,
                    sumOrNull(over(p => (building ? p.component.totalArea(building) : null))),
                    sumOrNull(over(p => p.component.linearMeters?.value ?? null)) ?? undefined,
                    unanimousOrNull(keys.map(key => draftComponents[key]?.replacementCostSource ?? 'template')) ?? 'template',
                    // Summed over the selection, like the area and length
                    keys.reduce((sum, key) => sum + unitCountOf(draftComponents[key]?.replacementCustomUnitCount), 0)
                )
                : null,
            replacementUnitCount: isBulk ? undefined : draftComponents[componentKey]?.replacementCustomUnitCount,
            onReplacementUnitCountChange: (count: number) => handleReplacementUnitCountChange(componentKey, count),
            onReplacementUnitCountClear: () => handleReplacementUnitCountClear(componentKey),
            getDisplayLifetime: lifetime ? unanimous(keys.map(key => getDisplayLifetime(key, lifetime))) : 'N/A',
            getDisplayMaintenanceInterval: (templateId: string) => {
                const template = templateOf(templateId);
                return template ? unanimous(overRow(templateId, key => getDisplayMaintenanceInterval(key, templateId, template))) : '';
            },
            getDisplayMaintenanceDate: (templateId: string) => {
                const template = templateOf(templateId);
                if (!template || !lifetime) return '';
                return unanimous(over(p => {
                    const nextDate = calculateNextMaintenanceDate(p.component, template, lifetime, building);
                    return getDisplayMaintenanceDate(p.component.key, templateId, nextDate);
                }));
            },
            getDisplayMaintenanceCost: (templateId: string) =>
                sumDisplayCosts(keys.map(key => getDisplayMaintenanceCost(key, templateId))),
            getDisplayBaseInterval: (templateId: string) => {
                const template = templateOf(templateId);
                return template ? getDisplayBaseInterval(componentKey, template) : { value: '', type: MaintenanceIntervalType.years };
            },
            getDisplayIntervalType: (templateId: string) => {
                const activity = firstActivity(templateId);
                if (activity?.intervalType) return activity.intervalType;
                const template = templateOf(templateId);
                const baseInterval = template ? getDisplayBaseInterval(componentKey, template) : null;
                return baseInterval?.type || MaintenanceIntervalType.years;
            },

            // Cost explanations
            getReplacementCostExplanation: isBulk
                ? `Summa för ${keys.length} komponenter`
                : getReplacementCostExplanation(componentKey),
            getMaintenanceCostExplanation: (templateId: string) => isBulk
                ? `Summa för ${rowKeys(templateId).length} komponenter`
                : getMaintenanceCostExplanation(componentKey, templateId),

            // Handlers, applied to every selected component
            onOpenConditionModal: () => handleOpenConditionModal(plans),
            onOpenLifetimeModal: () => {
                if (specificTypeValue) {
                    handleOpenLifetimeModal(specificTypeValue);
                }
            },
            onReplacementYearChange: (value: string) => each(key => handleReplacementYearChange(key, value)),
            onReplacementYearBlur: () => each(handleReplacementYearBlur),
            onReplacementCostChange: (value: string) => each(key => handleReplacementCostChange(key, value)),
            onReplacementCostBlur: () => each(handleReplacementCostBlur),
            onMaintenanceRemove: (templateId: string) =>
                handleMaintenanceActivityRemove(keys, templateId, templates.some(t => t.id === templateId)),
            onAddRowToAll: (templateId: string) =>
                keys.filter(key => activityOf(key, templateId) === undefined)
                    .forEach(key => handleMaintenanceActivityAdd(key, templateId)),
            onMaintenanceIntervalChange: (templateId: string, value: string) =>
                eachRow(templateId, key => handleMaintenanceIntervalChange(key, templateId, value)),
            onMaintenanceIntervalBlur: (templateId: string) =>
                eachRow(templateId, key => handleMaintenanceIntervalBlur(key, templateId)),
            onMaintenanceIntervalTypeChange: (templateId: string, type: MaintenanceIntervalType) =>
                eachRow(templateId, key => handleMaintenanceIntervalTypeChange(key, templateId, type)),
            onMaintenanceDateChange: (templateId: string, value: string) =>
                eachRow(templateId, key => handleMaintenanceDateChange(key, templateId, value)),
            onMaintenanceDateBlur: (templateId: string) =>
                eachRow(templateId, key => handleMaintenanceDateBlur(key, templateId)),
            onMaintenanceCostChange: (templateId: string, value: string) =>
                eachRow(templateId, key => handleMaintenanceCostChange(key, templateId, value)),
            onMaintenanceCostBlur: (templateId: string) =>
                eachRow(templateId, key => handleMaintenanceCostBlur(key, templateId)),
            // A mixed selection is brought along, a uniform one switched off
            onDoneToggle: () => keysToToggle(keys, draftComponents, 'isDone').forEach(handleDoneToggle),
            onExcludeToggle: () => keysToToggle(keys, draftComponents, 'excluded').forEach(handleExcludeToggle),
            onOpenNewActivityModal: () => {
                if (specificTypeValue) {
                    handleOpenNewActivityModal(specificTypeValue, keys);
                }
            },
            onOpenCostSettingsModal: (templateId: string) => {
                const template = templateOf(templateId);
                const targets = rowKeys(templateId);
                const first = activityOf(targets[0], templateId);
                if (!template || !first || !specificTypeValue) return;

                // The modal starts from the rows' settings when they agree, else from the template's
                const settingsAgree = unanimousOrNull(targets.map(key => costSettingsKey(activityOf(key, templateId)))) !== null;
                const activity = settingsAgree ? first : withoutCostSettings(first);

                const targetPlans = plans.filter(p => targets.includes(p.component.key));
                const quantities = targetPlans.map(p => {
                    if (template.cost_unit === 'per_unit') return null;
                    return rowQuantity(p, templateId);
                });
                const quantity = template.cost_unit === 'per_sqm' || template.cost_unit === 'per_lm'
                    ? sumOrNull(quantities)
                    : sumOrNull(targetPlans.map(p => unitCountOf(activityOf(p.component.key, templateId)?.customUnitCount)));
                const calculatedCost = building
                    ? targetPlans.reduce((sum, p) => {
                        const own = activityOf(p.component.key, templateId);
                        const customQuantity = own?.customQuantity !== undefined && own.customQuantity > 0 ? own.customQuantity : undefined;
                        return sum + calculateActivityCost(p.component, template, building, customQuantity, undefined, own?.customUnitCount);
                    }, 0)
                    : 0;

                handleOpenCostSettingsModal({
                    componentKey: targets[0],
                    componentKeys: targets,
                    templateId,
                    templateName: template.name,
                    activity,
                    calculatedCost,
                    quantity,
                    costExplanation: isBulk ? `Summa för ${targets.length} komponenter` : getMaintenanceCostExplanation(componentKey, templateId),
                    componentType: specificTypeValue,
                    componentTypeName: getTranslation(specificTypeValue, selectedPlan.component.type),
                    templateCost: template.cost || 0,
                    templateCostUnit: template.cost_unit || 'per_unit',
                    templatePrimaryComponentType: template.primaryComponentType
                });
            },
            onOpenReplacementCostModal: () => {
                if (!lifetime || !specificTypeValue) return;

                const quantity = lifetime.costUnit === 'per_sqm'
                    ? sumOrNull(over(p => (building ? p.component.totalArea(building) : null)))
                    : lifetime.costUnit === 'per_lm'
                        ? sumOrNull(over(p => p.component.linearMeters?.value ?? null))
                        : keys.reduce((sum, key) => sum + unitCountOf(draftComponents[key]?.replacementCustomUnitCount), 0);
                const calculatedCost = building
                    ? plans.reduce((sum, p) => sum + calculateReplacementCost(
                        p.component, lifetime, building, undefined,
                        draftComponents[p.component.key]?.replacementCustomUnitCount
                    ), 0)
                    : 0;
                const settingsAgree = unanimousOrNull(keys.map(key => replacementSettingsKey(draftComponents[key]))) !== null;

                handleOpenReplacementCostModal({
                    componentKey,
                    componentKeys: keys,
                    componentName: isBulk
                        ? `${keys.length} komponenter`
                        : (building ? selectedPlan.component.displayName(building) : 'Komponent'),
                    componentData: settingsAgree
                        ? draftComponents[componentKey] ?? { maintenanceActivities: {} }
                        : { maintenanceActivities: {} },
                    calculatedCost,
                    quantity,
                    costExplanation: isBulk ? `Summa för ${keys.length} komponenter` : getReplacementCostExplanation(componentKey),
                    componentType: specificTypeValue,
                    componentTypeName: getTranslation(specificTypeValue, selectedPlan.component.type),
                    templateCost: lifetime.replacementCost || 0,
                    templateCostUnit: lifetime.costUnit || 'per_unit'
                });
            },
            onOpenIntervalSettingsModal: (templateId: string) => {
                const template = templateOf(templateId);
                if (!template) return;
                const targets = rowKeys(templateId);

                // Get template interval (considering directional intervals, direction inherited from parents)
                const cardinalDirection = resolveCardinalDirection(selectedPlan.component, building);
                const directionalInterval = getIntervalForDirection(template, cardinalDirection);
                const templateIntervalValue = directionalInterval.interval_value;
                const templateIntervalType = directionalInterval.interval_type;

                // Get current interval (from draft or template); the template's when the rows disagree
                const agreed = unanimousOrNull(targets.map(key => {
                    const activity = activityOf(key, templateId);
                    return JSON.stringify([activity?.intervalValue, activity?.intervalType]);
                }));
                const activity = agreed !== null ? activityOf(targets[0], templateId) : undefined;
                const currentIntervalValue = activity?.intervalValue ?? templateIntervalValue;
                const currentIntervalType = activity?.intervalType ?? templateIntervalType;

                // Check if custom
                const isCustom = activity?.intervalValue !== undefined &&
                    (activity.intervalValue !== templateIntervalValue || activity.intervalType !== templateIntervalType);

                handleOpenIntervalSettingsModal({
                    componentKey: targets[0],
                    componentKeys: targets,
                    templateId,
                    templateName: template.name,
                    templateIntervalValue,
                    templateIntervalType,
                    cardinalDirection,
                    hasDirectionalInterval: hasIntervalForDirection(template, cardinalDirection),
                    currentIntervalValue,
                    currentIntervalType,
                    isCustomInterval: isCustom
                });
            },
            onOpenEditTemplateModal: handleOpenEditTemplateModal
                && ((template: MaintenanceTemplate) => handleOpenEditTemplateModal(template, rowKeys(template.id))),
            onOpenAreaModal: () => handleOpenAreaModal(selectedPlan),
            onOpenBulkApplyModal: () => handleOpenBulkApplyModal(componentKey),
            hasBulkApplyTargets,
            onOpenCopyActivitiesModal: () => handleOpenCopyActivitiesModal(componentKey),
            hasCopyActivitiesSources,
            coveringChecks,
            onOpenAddCheck: () => handleOpenAddCheck(keys),
            onRemoveCheck: handleRemoveCheck,
            onEditCheckDate: handleEditCheckDate,

            // Custom quantity handlers: a per_unit row's Mängd is its antal (customUnitCount). Per component, never in bulk
            onQuantityChange: (templateId: string, quantity: number) =>
                isPerUnitTemplate(templateId)
                    ? handleActivityUnitCountChange(componentKey, templateId, quantity)
                    : handleActivityQuantityChange(componentKey, templateId, quantity),
            onQuantityClear: (templateId: string) =>
                isPerUnitTemplate(templateId)
                    ? handleActivityUnitCountClear(componentKey, templateId)
                    : handleActivityQuantityClear(componentKey, templateId),
            getActivityCustomQuantity: (templateId: string) => {
                const activity = activityOf(componentKey, templateId);
                return isPerUnitTemplate(templateId) ? activity?.customUnitCount : activity?.customQuantity;
            },
            getActivityComment: (templateId: string) => {
                const comments = overRow(templateId, key => activityOf(key, templateId)?.comment ?? '');
                const comment = unanimousOrNull(comments);
                if (comment === null) return unanimous(comments);
                return comment || undefined;
            },
            hasActivityEntry: (templateId: string) => rowPresence(keys, draftComponents, templateId).present > 0,
            onCommentChange: (templateId: string, text: string) =>
                eachRow(templateId, key => handleActivityCommentChange(key, templateId, text)),
            getDisplayArea: (templateId: string) => {
                if (!templateOf(templateId)) return '–';
                const quantities = over(p => rowQuantity(p, templateId)).filter((q): q is number => q !== null);
                if (quantities.length === 0) return '–';
                const total = quantities.reduce((sum, q) => sum + q, 0);
                return `${total.toLocaleString('sv-SE')} ${unitLabelOf(templateId)}`;
            },
            getComponentQuantity: (templateId: string) => {
                const template = templateOf(templateId);
                if (template?.cost_unit === 'per_unit') return 1;
                if (template?.cost_unit === 'per_lm') {
                    return selectedPlan.component.linearMeters?.value || null;
                }
                return building ? selectedPlan.component.totalArea(building) : null;
            },
            getActivityHasCustomCost: (templateId: string) =>
                rowKeys(templateId).every(key => activityOf(key, templateId)?.costSource === 'custom'),
            getActivityCostBasis: (templateId: string) => ({
                source: unanimousOrNull(overRow(templateId, key => activityOf(key, templateId)?.costSource ?? 'template')) ?? 'template',
                hasAttachments: rowKeys(templateId).some(key => Object.keys(activityOf(key, templateId)?.costAttachments ?? {}).length > 0)
            }),
            getTemplateCostUnit: (templateId: string) => {
                const template = templateOf(templateId);
                return template?.cost_unit || 'per_unit';
            },

            // UI state
            highlightedActivity,
            setHighlightedActivity,
            activeTooltip,
            setActiveTooltip,

            // Maintenance checking
            isDueThisYear: (nextDate: Date | null) =>
                nextDate ? nextDate.getFullYear() === new Date().getFullYear() : false,
            isCustomInterval: (templateId: string) => {
                const template = templateOf(templateId);
                if (!template) return false;
                return plans.some(p => {
                    const activity = activityOf(p.component.key, templateId);
                    if (!activity?.intervalValue) return false;
                    const directionalInterval = getIntervalForDirection(template, resolveCardinalDirection(p.component, building));
                    return activity.intervalValue !== directionalInterval.interval_value ||
                        activity.intervalType !== directionalInterval.interval_type;
                });
            },
            calculateNextMaintenanceDate: (templateId: string) => {
                // Check for user-set date in draft
                const activity = firstActivity(templateId);
                if (activity?.nextDate) {
                    return new Date(activity.nextDate);
                }
                // Default to current date if no user-set date
                return new Date();
            }
        };
    }, [
        componentKey,
        selectedPlan,
        plans,
        keys,
        isBulk,
        buildingKey,
        building,
        templates,
        allTemplates,
        isPerUnitTemplate,
        lifetime,
        draftComponents,
        highlightedActivity,
        setHighlightedActivity,
        activeTooltip,
        setActiveTooltip,
        specificTypeValue,
        getTranslation,
        handleOpenConditionModal,
        handleOpenLifetimeModal,
        handleOpenNewActivityModal,
        handleOpenCostSettingsModal,
        handleOpenReplacementCostModal,
        handleOpenIntervalSettingsModal,
        handleOpenEditTemplateModal,
        handleOpenAreaModal,
        handleOpenBulkApplyModal,
        hasBulkApplyTargets,
        handleOpenCopyActivitiesModal,
        hasCopyActivitiesSources,
        coveringChecks,
        handleOpenAddCheck,
        handleRemoveCheck,
        handleEditCheckDate,
        handleActivityQuantityChange,
        handleActivityQuantityClear,
        handleActivityUnitCountChange,
        handleActivityUnitCountClear,
        handleReplacementUnitCountChange,
        handleReplacementUnitCountClear,
        handleActivityCommentChange,
        handleReplacementYearChange,
        handleReplacementYearBlur,
        handleReplacementCostChange,
        handleReplacementCostBlur,
        handleMaintenanceActivityRemove,
        handleMaintenanceActivityAdd,
        handleMaintenanceIntervalChange,
        handleMaintenanceIntervalBlur,
        handleMaintenanceIntervalTypeChange,
        handleMaintenanceDateChange,
        handleMaintenanceDateBlur,
        handleMaintenanceCostChange,
        handleMaintenanceCostBlur,
        handleDoneToggle,
        handleExcludeToggle,
        getDisplayReplacementYear,
        getDisplayReplacementCost,
        getDisplayLifetime,
        getDisplayMaintenanceInterval,
        getDisplayMaintenanceDate,
        getDisplayMaintenanceCost,
        getDisplayBaseInterval,
        getReplacementCostExplanation,
        getMaintenanceCostExplanation
    ]);

    return (
        <ComponentCardContext.Provider value={value}>
            {children}
        </ComponentCardContext.Provider>
    );
};
